import React from "react";
import {
  Shield,
  Mail,
  Eye,
  Clock,
  Cookie as CookieIcon,
  Settings,
  Monitor,
} from "lucide-react";

const Cookie = () => {

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section
        className="bg-cream pt-16 pb-16 md:pt-24 md:pb-20 px-6 text-center"
      >
        <div className="max-w-4xl mx-auto">
          <p className="text-primary text-xs md:text-sm font-medium tracking-[0.22em] uppercase mb-5">
            Legal
          </p>
          <h1 className="font-display font-light text-ink leading-[1.1] text-4xl md:text-5xl mb-8">
            Cookie Policy
          </h1>
          <div className="bg-white rounded-2xl border border-line p-6 text-left max-w-2xl mx-auto">
            <div className="space-y-2 text-sm text-muted">
              <p>
                <strong className="text-ink font-medium">
                  Effective date:
                </strong>{" "}
                02 May 2024
              </p>
              <p>
                <strong className="text-ink font-medium">
                  Last updated:
                </strong>{" "}
                02 May 2024
              </p>
              <p>
                <strong className="text-ink font-medium">
                  Contact for this policy:
                </strong>{" "}
                info@weicarehome.com
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <p className="text-muted leading-relaxed mb-8">
            This Cookie Policy explains how <strong>Wei Care</strong>{" "}
            ("we", "us", "our") uses cookies and similar technologies on our
            website, client portals, and emails. It works together with our
            Privacy Policy.
          </p>

          <div className="bg-cream rounded-2xl p-8 mb-8 border border-line">
            <div className="space-y-3 text-muted">
              <p>
                <strong>Website / domain:</strong> weicarehome.com
              </p>
              <p>
                <strong>Business name:</strong> Weicare Pty Ltd, trading as Wei Care (ABN 40 643 964 121)
              </p>
              <p>
                <strong>Address:</strong> 15 Monash Street, Clermont QLD 4721
              </p>
              <p>
                <strong>Phone:</strong> +61 420 162 243
              </p>
              <p>
                <strong>Email:</strong> info@weicarehome.com
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What are cookies */}
      <section className="py-16 px-6 bg-cream">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-display font-light text-ink text-3xl md:text-4xl mb-8 flex items-center">
            <CookieIcon
              className="w-8 h-8 mr-3 text-primary"
              strokeWidth={1}
            />
            1) What are cookies and similar technologies
          </h2>

          <div className="space-y-4 text-muted">
            <p>
              <strong>Cookies</strong> are small text files placed on your
              device when you visit a website.
            </p>
            <p>
              <strong>Pixels and tags</strong> are small pieces of code that
              help us understand usage and the success of features or emails.
            </p>
            <p>
              <strong>Local storage and session storage</strong> allow a site to
              store data in your browser.
            </p>
            <p>
              <strong>SDKs</strong> are similar tools used in mobile apps.
            </p>
            <p>
              These technologies help a site function, keep you signed in,
              remember choices, measure performance and, where enabled,
              personalise content or ads.
            </p>
          </div>
        </div>
      </section>

      {/* Types of cookies */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-display font-light text-ink text-3xl md:text-4xl mb-8">
            2) Types of cookies we use
          </h2>
          <p className="text-muted mb-8">
            We group cookies by purpose. You can manage non-essential categories
            in the Cookie Settings panel.
          </p>

          <div className="space-y-6">
            {[
              {
                type: "Strictly necessary",
                description:
                  "Required for the site to work. Examples: page navigation, loading resources, security, forms, fraud prevention, cookie consent storage.",
                note: "Always active because they are essential.",
                icon: <Shield className="w-6 h-6" strokeWidth={1} />,
                color: "red",
              },
              {
                type: "Performance and analytics",
                description:
                  "Help us understand how the site is used so we can improve it. Examples: page views, time on page, error logs, campaign attribution. Data is aggregated where possible. IPs may be truncated or anonymised when configured.",
                icon: <Monitor className="w-6 h-6" strokeWidth={1} />,
                color: "blue",
              },
              {
                type: "Functionality",
                description:
                  "Remember your preferences. Examples: language, region, saved form inputs, accessibility settings.",
                icon: <Settings className="w-6 h-6" strokeWidth={1} />,
                color: "green",
              },
              {
                type: "Advertising and social media",
                description:
                  "Show or measure ads, build audiences, or allow social sharing. May track you across sites. We only use these with your consent where required by law.",
                icon: <Eye className="w-6 h-6" strokeWidth={1} />,
                color: "purple",
              },
              {
                type: "Email tracking pixels",
                description:
                  "If you opt in to emails, we may use pixels to measure opens and link clicks. You can disable images in your email client to limit this.",
                icon: <Mail className="w-6 h-6" strokeWidth={1} />,
                color: "yellow",
              },
            ].map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 border border-line"
              >
                <div className="flex items-start">
                  <div className="bg-accent-soft p-2 rounded-lg mr-4 text-primary flex-shrink-0">
                    {item.icon}
                  </div>
                  <div>
                    <h3 className="font-display font-normal text-ink mb-2">
                      {item.type}
                    </h3>
                    <p className="text-muted text-sm mb-2">
                      {item.description}
                    </p>
                    {item.note && (
                      <p className="text-primary text-sm font-medium">
                        {item.note}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* First-party vs third-party */}
      <section className="py-16 px-6 bg-cream">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-display font-light text-ink text-3xl md:text-4xl mb-8">
            3) First-party vs third-party
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl p-6 border border-line">
              <h3 className="font-display font-normal text-ink mb-3">
                First-party cookies
              </h3>
              <p className="text-muted">are set by weicarehome.com.</p>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-line">
              <h3 className="font-display font-normal text-ink mb-3">
                Third-party cookies
              </h3>
              <p className="text-muted">
                are set by service providers, for example analytics or embedded
                video. Third parties may treat the data they collect as personal
                data under applicable law.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Duration */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-display font-light text-ink text-3xl md:text-4xl mb-8">
            4) Duration
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl p-6 border border-line">
              <h3 className="font-display font-normal text-ink mb-3">
                Session cookies
              </h3>
              <p className="text-muted">delete when you close your browser.</p>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-line">
              <h3 className="font-display font-normal text-ink mb-3">
                Persistent cookies
              </h3>
              <p className="text-muted">
                stay for a set time or until you delete them. We set reasonable
                lifetimes and review them periodically.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Legal basis */}
      <section className="py-16 px-6 bg-cream">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-display font-light text-ink text-3xl md:text-4xl mb-8">
            5) Legal basis for cookies
          </h2>
          <div className="space-y-4">
            <div className="bg-white rounded-2xl p-6 border border-line">
              <h3 className="font-display font-normal text-ink mb-2">
                Strictly necessary
              </h3>
              <p className="text-muted text-sm">
                our legitimate interests in running a secure and functional site
                (and/or performance of a contract where applicable).
              </p>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-line">
              <h3 className="font-display font-normal text-ink mb-2">
                Performance, functionality, advertising, social
              </h3>
              <p className="text-muted text-sm">
                your consent where required under applicable law. In some cases
                we may rely on our legitimate interests for limited,
                privacy-protective analytics. You can withdraw consent at any
                time in Cookie Settings.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Your choices */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-display font-light text-ink text-3xl md:text-4xl mb-8">
            6) Your choices
          </h2>

          <div className="space-y-8">
            <div>
              <h3 className="font-display font-normal text-ink text-xl mb-4">
                A) Cookie Settings panel
              </h3>
              <p className="text-muted">
                Click Cookie Settings on our site to review, accept, or reject
                categories. Your choices apply to the browser and device you
                use. If you clear cookies or use a different device or browser,
                you will need to set preferences again.
              </p>
            </div>

            <div>
              <h3 className="font-display font-normal text-ink text-xl mb-4">
                B) Browser controls
              </h3>
              <p className="text-muted">
                Most browsers let you block or delete cookies under Privacy or
                Security settings. Blocking strictly necessary cookies may break
                site features.
              </p>
            </div>

            <div>
              <h3 className="font-display font-normal text-ink text-xl mb-4">
                C) Do Not Track and Global Privacy Control
              </h3>
              <div className="space-y-3 text-muted">
                <p>
                  <strong>Do Not Track (DNT):</strong> there is no standard we
                  can apply consistently, so we do not respond to DNT.
                </p>
                <p>
                  <strong>Global Privacy Control (GPC):</strong> where required
                  by law, we treat GPC as a request to opt out of the sale or
                  sharing of personal information for cross-context behavioural
                  advertising. You can still adjust Cookie Settings for more
                  granular control.
                </p>
              </div>
            </div>

            <div>
              <h3 className="font-display font-normal text-ink text-xl mb-4">
                D) Advertising choices
              </h3>
              <p className="text-muted">
                You can opt out of interest-based advertising from many
                providers at network opt-out pages in your country (e.g.,
                youradchoices.com or youronlinechoices.eu). This limits targeted
                ads but does not remove ads.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Common cookies table */}
      <section className="py-16 px-6 bg-cream">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-display font-light text-ink text-3xl md:text-4xl mb-8">
            7) Cookies we commonly use
          </h2>
          <p className="text-muted mb-8">
            Replace or remove rows to reflect the tools you actually deploy.
          </p>

          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-2xl border border-line overflow-hidden">
              <thead>
                <tr className="bg-cream">
                  <th className="px-4 py-3 text-left font-display font-normal text-ink">
                    Category
                  </th>
                  <th className="px-4 py-3 text-left font-display font-normal text-ink">
                    Name / Example
                  </th>
                  <th className="px-4 py-3 text-left font-display font-normal text-ink">
                    Purpose
                  </th>
                  <th className="px-4 py-3 text-left font-display font-normal text-ink">
                    Typical lifetime
                  </th>
                  <th className="px-4 py-3 text-left font-display font-normal text-ink">
                    Party
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {[
                  [
                    "Strictly necessary",
                    "cookie_consent_status",
                    "remembers your consent choices",
                    "6 to 12 months",
                    "first-party",
                  ],
                  [
                    "Strictly necessary",
                    "__cf_bm (or similar)",
                    "bot management/security (if using a CDN)",
                    "up to 30 minutes",
                    "third-party (CDN)",
                  ],
                  [
                    "Strictly necessary",
                    "session_id",
                    "keeps you signed in / session state",
                    "session",
                    "first-party",
                  ],
                  [
                    "Performance & analytics",
                    "_ga, _gid, _gat or GA4 equivalents",
                    "Google Analytics identifiers/throttling",
                    "1 minute to 24 months",
                    "third-party (Google)",
                  ],
                  [
                    "Performance & analytics",
                    "_clck, _clsk",
                    "Microsoft Clarity session & clickstream",
                    "session to 12 months",
                    "third-party (Microsoft)",
                  ],
                  [
                    "Performance & analytics",
                    "log_level / error_state",
                    "error tracking & stability",
                    "session to 12 months",
                    "first-party / third-party (e.g., Sentry)",
                  ],
                  [
                    "Functionality",
                    "locale / lang",
                    "remembers language/region",
                    "6 to 12 months",
                    "first-party",
                  ],
                  [
                    "Functionality",
                    "remember_me",
                    "saves login/form preference",
                    "30 days to 12 months",
                    "first-party",
                  ],
                  [
                    "Advertising & social",
                    "_fbp",
                    "Facebook pixel for measurement",
                    "~3 months",
                    "third-party (Meta)",
                  ],
                  [
                    "Advertising & social",
                    "_tt_enable_cookie, _ttp",
                    "TikTok pixel",
                    "up to 13 months",
                    "third-party (TikTok)",
                  ],
                  [
                    "Advertising & social",
                    "li_fat_id",
                    "LinkedIn Insights",
                    "up to 6 months",
                    "third-party (LinkedIn)",
                  ],
                  [
                    "Advertising & social / embeds",
                    "yt_innertube_*, VISITOR_INFO1_LIVE",
                    "YouTube embed preferences/measurement",
                    "up to 6 months",
                    "third-party (Google)",
                  ],
                  [
                    "Email pixels",
                    "ESP open/click trackers",
                    "measure engagement in emails",
                    "per email",
                    "third-party ESP",
                  ],
                ].map((row, index) => (
                  <tr key={index} className="hover:bg-cream">
                    <td className="px-4 py-3 text-sm text-muted font-medium">
                      {row[0]}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted font-mono">
                      {row[1]}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted">{row[2]}</td>
                    <td className="px-4 py-3 text-sm text-muted">{row[3]}</td>
                    <td className="px-4 py-3 text-sm text-muted">{row[4]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-muted text-sm mt-4">
            We may update this list as our site changes. Material changes will
            be reflected in this policy.
          </p>
        </div>
      </section>

      {/* Consent collection */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-display font-light text-ink text-3xl md:text-4xl mb-8">
            8) When consent is required and how we collect it
          </h2>
          <div className="space-y-4 text-muted">
            <p>
              Where consent is required, we obtain it before placing or reading
              non-essential cookies.
            </p>
            <p>
              Our banner presents clear options: <strong>Accept all</strong>,{" "}
              <strong>Reject non-essential</strong>, and{" "}
              <strong>Manage settings</strong>.
            </p>
            <p>
              We record consent choices and the timestamp to demonstrate
              compliance.
            </p>
            <p>
              If you continue browsing after rejecting non-essential cookies,
              only strictly necessary cookies will be used.
            </p>
          </div>
        </div>
      </section>

      {/* Privacy notes */}
      <section className="py-16 px-6 bg-cream">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-display font-light text-ink text-3xl md:text-4xl mb-8">
            9) Your privacy rights
          </h2>
          <div className="space-y-4 text-muted">
            <p>
              We handle personal information in line with the Privacy Act 1988
              (Cth) and the Australian Privacy Principles. Some cookies used for
              advertising or analytics may collect information that is treated
              as personal information under these laws.
            </p>
            <p>
              To limit non-essential cookies, adjust the Cookie Settings panel
              at any time. We also honour Global Privacy Control signals where
              required.
            </p>
            <p>
              Your rights are described in our Privacy Policy and may include
              access to, and correction of, the personal information we hold
              about you.
            </p>
          </div>
        </div>
      </section>

      {/* Data collection */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-display font-light text-ink text-3xl md:text-4xl mb-8">
            10) Data we collect through cookies
          </h2>
          <p className="text-muted">
            Depending on your settings, cookies may collect: device and browser
            type, operating system, IP address, general location, pages viewed,
            buttons clicked, time spent, referring URLs, and campaign
            identifiers. We use aggregation and de-identification where
            possible, and retention periods that match the purpose.
          </p>
        </div>
      </section>

      {/* Retention */}
      <section className="py-16 px-6 bg-cream">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-display font-light text-ink text-3xl md:text-4xl mb-8 flex items-center">
            <Clock className="w-8 h-8 mr-3 text-primary" strokeWidth={1} />
            11) Retention
          </h2>
          <p className="text-muted">
            We keep cookie data only as long as needed for each purpose.
            Persistent cookies have fixed lifetimes shown in the table.
            Analytics data is retained according to our analytics provider
            settings and our data retention policy.
          </p>
        </div>
      </section>

      {/* Children */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-display font-light text-ink text-3xl md:text-4xl mb-8">
            12) Children
          </h2>
          <p className="text-muted">
            Our site is not directed to children. We process a child's
            information only when necessary to support the care of a young
            person we assist, and only through a parent or legal guardian, never
            for advertising.
          </p>
        </div>
      </section>

      {/* Updates */}
      <section className="py-16 px-6 bg-cream">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-display font-light text-ink text-3xl md:text-4xl mb-8">
            13) Updates to this policy
          </h2>
          <p className="text-muted">
            We may update this Cookie Policy from time to time. Material changes
            will be posted on this page with a new effective date. If required
            by law, we will ask for consent again.
          </p>
        </div>
      </section>

      {/* Contact */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-display font-light text-ink text-3xl md:text-4xl mb-8">
            14) How to contact us
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-6 border border-line">
              <h3 className="font-display font-normal text-ink mb-3">
                Email
              </h3>
              <a
                href="mailto:info@weicarehome.com"
                className="text-primary hover:text-primary-dark transition-colors"
              >
                info@weicarehome.com
              </a>
            </div>

            <div className="bg-white rounded-2xl p-6 border border-line">
              <h3 className="font-display font-normal text-ink mb-3">
                Phone
              </h3>
              <a
                href="tel:+61480804640"
                className="text-primary hover:text-primary-dark transition-colors"
              >
                +61 420 162 243
              </a>
            </div>

            <div className="bg-white rounded-2xl p-6 border border-line">
              <h3 className="font-display font-normal text-ink mb-3">
                Postal
              </h3>
              <p className="text-muted text-sm">
                Weicare Pty Ltd, 15 Monash Street, Clermont QLD 4721
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Optional banner copy */}
      <section className="py-16 px-6 bg-cream">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-display font-light text-ink text-3xl md:text-4xl mb-8">
            15) Optional banner and settings copy
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-6 border border-line">
              <h3 className="font-display font-normal text-ink mb-3">
                Banner title
              </h3>
              <p className="text-muted">Your privacy choices</p>
            </div>

            <div className="bg-white rounded-2xl p-6 border border-line">
              <h3 className="font-display font-normal text-ink mb-3">
                Banner text
              </h3>
              <p className="text-muted">
                We use cookies to run the site, improve performance, and, if you
                agree, to personalise content and measure marketing. You can
                make changes at any time in Cookie Settings.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-6 border border-line">
              <h3 className="font-display font-normal text-ink mb-3">
                Buttons
              </h3>
              <p className="text-muted">
                Accept all • Reject non-essential • Cookie Settings
              </p>
            </div>

            <div className="bg-white rounded-2xl p-6 border border-line">
              <h3 className="font-display font-normal text-ink mb-3">
                Settings categories and examples
              </h3>
              <ul className="space-y-2 text-muted text-sm">
                <li>
                  <strong>Strictly necessary:</strong> security, load balancing,
                  form submission
                </li>
                <li>
                  <strong>Performance:</strong> analytics to improve site
                  features
                </li>
                <li>
                  <strong>Functionality:</strong> language, saved preferences
                </li>
                <li>
                  <strong>Advertising and social:</strong> ad measurement,
                  audiences, share buttons
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-2xl p-6 border border-line">
              <h3 className="font-display font-normal text-ink mb-3">
                Footer links
              </h3>
              <p className="text-muted">
                Cookie Settings • Cookie Policy • Do Not Sell or Share My
                Personal Information
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Implementation tips */}
      <section className="py-16 px-6 bg-cream">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-display font-light text-ink text-3xl md:text-4xl mb-8">
            16) Implementation tips for your developer
          </h2>

          <div className="space-y-4 text-muted">
            <p>
              • Load strictly necessary scripts by default. Gate all other tags
              behind consent categories.
            </p>
            <p>
              • Delay firing of analytics/advertising tags until consent is
              recorded (e.g., Google Consent Mode v2 / equivalent).
            </p>
            <p>
              • Store consent in a first-party cookie and, where relevant, in
              your CMP and ad/analytics tools.
            </p>
            <p>
              • Honour GPC signals by mapping them to an opt-out state at page
              load.
            </p>
            <p>
              • Provide a visible Cookie Settings link on every page (header or
              footer).
            </p>
            <p>
              • Maintain a changelog of vendors and cookie lifetimes; review at
              least quarterly.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Cookie;
